import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import BrandMark from "../components/BrandMark";
import api from "../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [mode, setMode] = useState(token ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleRequest = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSuccess(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong.");
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setSuccess(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired reset link.");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="reset-success">
        <div className="w-full max-w-sm text-center">
          <CheckCircle className="w-16 h-16 text-[#00CC52] mx-auto mb-6" />
          <h1 className="text-2xl font-black tracking-tight mb-3">{mode === "reset" ? "Password Updated" : "Check Your Email"}</h1>
          <p className="text-muted-foreground text-sm mb-8">{success}</p>
          <Link to="/login">
            <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-8 py-5 font-bold" data-testid="reset-to-login">
              Go to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <div className="mb-8">
          <BrandMark className="h-9" />
        </div>

        {mode === "request" ? (
          <>
            <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="reset-heading">Forgot Password</h1>
            <p className="text-muted-foreground text-sm mb-8">Enter your email and we'll send you a link to reset your password.</p>
            {error && <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-6 text-sm">{error}</div>}
            <form onSubmit={handleRequest} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com" className="mt-1.5 rounded-none bg-card border-border" required data-testid="reset-email-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none py-5 font-bold" data-testid="reset-submit">
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tight mb-2">Set New Password</h1>
            <p className="text-muted-foreground text-sm mb-8">Enter your new password below.</p>
            {error && <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-6 text-sm">{error}</div>}
            <form onSubmit={handleReset} className="space-y-5">
              <div>
                <Label className="text-sm font-medium">New Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters" className="mt-1.5 rounded-none bg-card border-border" required data-testid="reset-new-password" />
              </div>
              <div>
                <Label className="text-sm font-medium">Confirm Password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password" className="mt-1.5 rounded-none bg-card border-border" required data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none py-5 font-bold" data-testid="reset-password-submit">
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
